import { Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { parentStudentStatus } from '~/pages/ParentEnrollment/parentStudentStatus';
import { gradeLabel } from '~/pages/EnrollChecklist/shared';

/** The parts of the record a parent can ask the school to change, one per card. */
export type HouseholdSection = 'guardians' | 'emergency' | 'pickup' | 'address' | 'church';

interface GuardianData { person: { id: string; firstName: string; lastName: string; email?: string | null; phone?: string | null }; isPrimary: boolean; }
interface ChildData { person: { id: string; firstName: string; lastName: string; fullName: string; dateOfBirth?: unknown; studentEnrollmentForYear?: { id: string; state: string; enrollmentKind: string; gradeLevel?: string | null } | null }; }
interface AddressData { street?: string | null; street2?: string | null; city?: string | null; state?: string | null; zip?: string | null; country?: string | null; }
interface EmergencyContact { name?: string | null; phone?: string | null; relationship?: string | null; }
interface ChurchAffiliation { churchName?: string | null; pastorName?: string | null; churchEmail?: string | null; }

interface ParentFamilyInfoProps {
  /** Omitted for read-only views; supplied when the parent may request changes. */
  onEditSection?: (section: HouseholdSection) => void;
  /**
   * Shows the pencil but refuses it. An administrator viewing as the family
   * should see the affordance the parent has — that is the whole point of the
   * view — without being able to file a request in the family's name.
   */
  editDisabled?: boolean;
  /** Only these cards, in the page's usual order; left out, every card and the students. */
  sections?: HouseholdSection[];
  family: {
    guardians?: GuardianData[];
    children?: ChildData[];
    emergencyContacts?: EmergencyContact[];
    authorizedPickup?: EmergencyContact[];
    churchAffiliation?: ChurchAffiliation | null;
    address?: AddressData | null;
  };
}

/**
 * A card title with the pencil that asks the school to change that one section.
 *
 * The pencil sits on the block it edits rather than at the top of the page, so a
 * parent who notices one stale phone number does not have to hunt for it in a
 * dialog covering the whole record.
 */
function SectionHeader({ title, section, onEditSection, editDisabled }: { title: string; section: HouseholdSection; onEditSection?: (section: HouseholdSection) => void; editDisabled?: boolean }) {
  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0">
      <CardTitle>{title}</CardTitle>
      {onEditSection ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 text-muted-foreground md:h-8 md:w-8"
          aria-label={`Request a change to ${title.toLowerCase()}`}
          title={editDisabled ? 'Only the family can request a change' : 'Request a change'}
          disabled={editDisabled}
          onClick={() => onEditSection(section)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ) : null}
    </CardHeader>
  );
}

/** Emergency contacts and authorized pickup are the same shape and read the same way. */
function ContactCard({ title, section, contacts, emptyText, onEditSection, editDisabled }: { title: string; section: HouseholdSection; contacts: EmergencyContact[]; emptyText: string; onEditSection?: (section: HouseholdSection) => void; editDisabled?: boolean }) {
  return (
    <Card>
      <SectionHeader title={title} section={section} onEditSection={onEditSection} editDisabled={editDisabled} />
      <CardContent className="space-y-3">
        {contacts.length === 0 ? (
          <p className="text-muted-foreground">{emptyText}</p>
        ) : contacts.map((ct, i) => (
          <div key={i} className="text-sm">
            <div className="font-medium">{ct.name}</div>
            <div className="text-muted-foreground">{[ct.relationship, ct.phone].filter(Boolean).join(' · ')}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function ParentFamilyInfo({ family, onEditSection, editDisabled, sections }: ParentFamilyInfoProps) {
  const shows = (section: HouseholdSection) => !sections || sections.includes(section);
  const guardians = family.guardians ?? [];
  const children = family.children ?? [];
  const contacts = family.emergencyContacts ?? [];
  const pickup = family.authorizedPickup ?? [];
  const church = family.churchAffiliation;
  const address = family.address;

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {shows('guardians') && (
      <Card>
        <SectionHeader title="Parents / Guardians" section="guardians" onEditSection={onEditSection} editDisabled={editDisabled} />
        <CardContent className="space-y-3">
          {guardians.length === 0 ? (
            <p className="text-muted-foreground">No guardians on record.</p>
          ) : guardians.map((g) => (
            <div key={g.person.id} className="text-sm">
              <div className="font-medium">
                <span>{g.person.firstName} {g.person.lastName}</span>
                {g.isPrimary ? <span className="text-muted-foreground"> · Primary</span> : null}
              </div>
              {g.person.email ? <div className="text-muted-foreground">{g.person.email}</div> : null}
              {g.person.phone ? <div className="text-muted-foreground">{g.person.phone}</div> : null}
            </div>
          ))}
        </CardContent>
      </Card>
      )}

      {!sections && (
      <Card>
        <CardHeader><CardTitle>Students</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {children.length === 0 ? (
            <p className="text-muted-foreground">No students on record.</p>
          ) : children.map((c) => {
            const se = c.person.studentEnrollmentForYear;
            return (
              <div key={c.person.id} className="flex items-center justify-between text-sm">
                <span className="font-medium">
                  <span>{c.person.fullName}</span>
                  {se?.gradeLevel ? <span className="text-muted-foreground"> · {gradeLabel(se.gradeLevel)}</span> : null}
                </span>
                {se ? <Badge variant="secondary">{parentStudentStatus(se.state)}</Badge> : <span className="text-muted-foreground">Not enrolled</span>}
              </div>
            );
          })}
        </CardContent>
      </Card>
      )}

      {shows('emergency') && (
      <ContactCard
        title="Emergency Contacts"
        section="emergency"
        contacts={contacts}
        emptyText="None on record."
        onEditSection={onEditSection}
        editDisabled={editDisabled}
      />
      )}

      {shows('pickup') && (
      <ContactCard
        title="Authorized Pickup"
        section="pickup"
        contacts={pickup}
        emptyText="Nobody besides the parents above."
        onEditSection={onEditSection}
        editDisabled={editDisabled}
      />
      )}

      {shows('address') && (
      <Card>
        <SectionHeader title="Home Address" section="address" onEditSection={onEditSection} editDisabled={editDisabled} />
        <CardContent className="text-sm text-muted-foreground">
          {address && (address.street || address.city) ? (
            <div>
              {address.street ? <div>{address.street}</div> : null}
              {address.street2 ? <div>{address.street2}</div> : null}
              <div>{[address.city, address.state, address.zip].filter(Boolean).join(', ')}</div>
              {address.country ? <div>{address.country}</div> : null}
            </div>
          ) : (
            <p>No address on record.</p>
          )}
        </CardContent>
      </Card>
      )}

      {shows('church') && (
      <Card>
        <SectionHeader title="Church Affiliation" section="church" onEditSection={onEditSection} editDisabled={editDisabled} />
        <CardContent className="text-sm text-muted-foreground">
          {church?.churchName || church?.pastorName ? (
            <div>
              {church.churchName ? <div className="font-medium text-foreground">{church.churchName}</div> : null}
              {church.pastorName ? <div>Pastor: {church.pastorName}</div> : null}
              {church.churchEmail ? <div>{church.churchEmail}</div> : null}
            </div>
          ) : (
            <p>None on record.</p>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
